import { useEffect, useState } from "react";
import { formatMoney } from "../../shared/money";
import { hasPermission } from "../../shared/permissions";
import { api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf, onSubmit } from "./ui";

type Field = { id: string; key: string; label: string; type: string; visibility: string; status: string; currency: string | null; entity_scope: string };
type Rule = { id: string; charge_key: string; name: string; status: string; version_status: string | null; config_json: string | null };
type Quote = { lines: { description: string; taxable_net_minor: number; client_visible: boolean; internal_cost_minor: number | null; charge_role: string | null }[]; totals: { net_minor: number } };

export function VariablesPage() {
  const { user } = useSession();
  const [fields, setFields] = useState<Field[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [events, setEvents] = useState<{ id: string; title: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const canField = hasPermission(user.role, user.permissions, "custom_fields.write");
  const canRule = hasPermission(user.role, user.permissions, "rules.publish");

  function load() {
    api<{ fields: Field[] }>("/api/custom-fields").then((result) => setFields(result.fields)).catch((reason: unknown) => setError(messageOf(reason)));
    api<{ rules: Rule[] }>("/api/price-rules").then((result) => setRules(result.rules)).catch(() => setRules([]));
    api<{ events: { id: string; title: string }[] }>("/api/events?limit=50").then((result) => setEvents(result.events)).catch(() => setEvents([]));
  }

  useEffect(load, []);

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Μεταβλητές και κανόνες</h1>
          <p className="lede">Η προσθήκη ενός πεδίου δεν χρεώνει τίποτα. Μόνο ένας δημοσιευμένος κανόνας μπορεί να κάνει μια τιμή χρέωση. Δεν υπάρχει γλώσσα τύπων ούτε τιμολόγηση με AI.</p>
        </div>
      </header>
      <Banner error={error} note={note} />
      <div className="split">
        <section className="panel">
          <h2>Πεδία</h2>
          {fields.length === 0 ? <p>Δεν υπάρχουν ακόμη προσαρμοσμένα πεδία. Το πάρκινγκ λείπει επίτηδες, για να το προσθέσεις εδώ.</p> : null}
          <ul>
            {fields.map((field) => (
              <li key={field.id}><code>{field.key}</code> · {field.label} · {field.type} · {field.visibility} · {field.status}</li>
            ))}
          </ul>
          {canField ? (
            <form onSubmit={onSubmit(async (body) => {
              setError(null);
              try {
                await api("/api/custom-fields", { method: "POST", body: JSON.stringify({ key: body.key, label: body.label || body.key, type: body.type, entity_scope: "event", visibility: body.visibility, currency: body.type === "money" ? "EUR" : null }) });
                setNote(`Το ${body.key} προστέθηκε. Δεν αλλάζει καμία προσφορά ώσπου να το χρησιμοποιήσει ένας κανόνας.`);
                load();
              } catch (reason) {
                setError(messageOf(reason));
              }
            })}>
              <div className="fields">
                <label>Κλειδί<input name="key" placeholder="parking_fee" required /></label>
                <label>Ετικέτα<input name="label" placeholder="Χρέωση πάρκινγκ" /></label>
                <label>Τύπος
                  <select name="type" defaultValue="money">
                    <option value="money">Χρήμα</option>
                    <option value="number">Αριθμός</option>
                    <option value="text">Κείμενο</option>
                    <option value="boolean">Ναι ή όχι</option>
                  </select>
                </label>
                <label>Ορατότητα
                  <select name="visibility" defaultValue="client">
                    <option value="client">Πελάτης</option>
                    <option value="staff">Προσωπικό</option>
                    <option value="internal_only">Μόνο εσωτερικά</option>
                  </select>
                </label>
              </div>
              <button type="submit">Προσθήκη μεταβλητής</button>
            </form>
          ) : null}
        </section>
        <section className="panel">
          <h2>Κανόνες τιμής</h2>
          <ul>
            {rules.map((rule) => <li key={`${rule.id}-${rule.version_status}`}>{rule.charge_key} · {rule.status} · {rule.version_status ?? "χωρίς έκδοση"}</li>)}
          </ul>
          {rules.length === 0 ? <p>Δεν υπάρχουν κανόνες τιμής ακόμη.</p> : null}
          {canRule ? (
            <form onSubmit={onSubmit(async (body) => {
              setError(null);
              const amount = Math.round(Number(body.amount_euros) * 100);
              if (!Number.isFinite(amount)) {
                setError("Γράψε τη χρέωση σε ευρώ.");
                return;
              }
              try {
                await api("/api/price-rules", {
                  method: "POST",
                  body: JSON.stringify({
                    charge_key: body.charge_key,
                    name: body.name || body.charge_key,
                    publish: true,
                    priority: 10,
                    scope: {},
                    config: {
                      type: "fixed_fee",
                      label: body.name || body.charge_key,
                      currency: "EUR",
                      client_visible: body.client_visible === "yes",
                      amount_minor: body.variable_key ? undefined : amount,
                      variable_key: body.variable_key || undefined,
                      cost_variable_key: body.cost_variable_key || undefined,
                    },
                  }),
                });
                setNote("Ο κανόνας δημοσιεύτηκε. Οι προτάσεις που έχουν ήδη σταλεί κρατούν το στιγμιότυπό τους.");
                load();
              } catch (reason) {
                setError(messageOf(reason));
              }
            })}>
              <div className="fields">
                <label>Κλειδί χρέωσης<input name="charge_key" placeholder="parking_fee" required /></label>
                <label>Ετικέτα<input name="name" placeholder="Πάρκινγκ" /></label>
                <label>Ποσό, EUR<input name="amount_euros" placeholder="25.00" /></label>
                <label>Αλλιώς, μεταβλητή χρημάτων<input name="variable_key" placeholder="parking_fee" /></label>
                <label>Μεταβλητή εσωτερικού κόστους<input name="cost_variable_key" placeholder="parking_cost" /></label>
                <label>Εμφάνιση στον πελάτη
                  <select name="client_visible" defaultValue="yes"><option value="yes">Ναι</option><option value="no">Οχι</option></select>
                </label>
              </div>
              <button type="submit">Δημοσίευση σταθερής χρέωσης</button>
            </form>
          ) : null}
        </section>
      </div>
      <section className="panel" style={{ marginTop: 16 }}>
        <h2>Τιμή σε εκδήλωση</h2>
        <form onSubmit={onSubmit(async (body) => {
          setError(null);
          try {
            const money = Math.round(Number(body.amount_euros) * 100);
            await api("/api/custom-values", { method: "PUT", body: JSON.stringify({ entity_type: "event", entity_id: body.event_id, values: { [body.key]: money } }) });
            const result = await api<{ quote: Quote }>("/api/quote/preview", { method: "POST", body: JSON.stringify({ event_id: body.event_id, venue_id: "ven_kolonaki", date: "2026-10-14", adults: 40, children: 0, package_id: "pkg_signature" }) });
            setQuote(result.quote);
            setNote("Η τιμή αποθηκεύτηκε. Η προεπισκόπηση χρησιμοποιεί τους δημοσιευμένους κανόνες.");
          } catch (reason) {
            setError(messageOf(reason));
          }
        })}>
          <div className="fields">
            <label>Εκδήλωση
              <select name="event_id">{events.map((event) => <option key={event.id} value={event.id}>{event.title}</option>)}</select>
            </label>
            <label>Κλειδί πεδίου<input name="key" defaultValue="parking_fee" /></label>
            <label>Ποσό, EUR<input name="amount_euros" defaultValue="25.00" /></label>
          </div>
          <button type="submit">Αποθήκευση και προεπισκόπηση</button>
        </form>
        {quote ? (
          <>
            <p>Καθαρή αξία προεπισκόπησης {formatMoney(quote.totals.net_minor)}. Οι γραμμές μόνο για εσωτερική χρήση δεν μπαίνουν στα έγγραφα του πελάτη.</p>
            <ul>
              {quote.lines.map((line) => (
                <li key={`${line.charge_role}-${line.description}`}>{line.description}: {formatMoney(line.taxable_net_minor)}{line.client_visible ? "" : " · κρυφό από τον πελάτη"}{line.internal_cost_minor == null ? "" : ` · κόστος ${formatMoney(line.internal_cost_minor)}`}</li>
              ))}
            </ul>
          </>
        ) : null}
      </section>
    </>
  );
}
